import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { FormattedMessage, useIntl } from 'react-intl';
import { useParams } from 'react-router-dom';

import { updateHiringGoalTargets } from '@/api-clients/update-hiring-goal-targets';
import ArrowRightIcon from '@/assets/icons/arrow-sm-right.svg';
import UserIcon from '@/assets/icons/user.svg';
import { useToaster } from '@/components/base/toaster';
import { DialogBody } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Typography } from '@/components/ui/typography';
import { useEditTargetsContext } from '@/contexts/edit-targets-context';
import { queryKeys } from '@/query-keys';
import { parseCurrency } from '@/utils/parse-currency';

import { BudgetUsageCard } from './budget-usage-card';
import { HiringGoalDetails } from './hiring-goal-details';
import { TargetBudgetInput } from './target-budget-input';
import { EditTargetsForm, HiringGoal } from './types';

interface FormProps {
  hiringGoal: HiringGoal;
  children: React.ReactNode;
  onFormChange: (enabled: boolean) => void;
  showSpinner: (isSaving: boolean) => void;
}

export function Form({
  children,
  onFormChange,
  hiringGoal,
  showSpinner,
}: FormProps) {
  const intl = useIntl();
  const { notifySuccess, notifyError } = useToaster();
  const { targets, closeEditTargetsDialog } = useEditTargetsContext();
  const queryClient = useQueryClient();
  const { openingSlug: currentOpeningSlug } = useParams();

  const hookForm = useForm<EditTargetsForm>({
    defaultValues: {
      budget_allotted: parseCurrency(targets?.budget) || undefined,
      expected_cpa: parseCurrency(targets?.cpa) || undefined,
      expected_conversion: targets?.conversion ?? undefined,
    },
  });

  const {
    register,
    handleSubmit,
    formState: { errors, dirtyFields, isDirty },
    getValues,
    watch,
  } = hookForm;

  const targetConversion = watch('expected_conversion');
  const targetBudget = watch('budget_allotted') ?? 0;
  const values = watch();

  useEffect(() => {
    // Enable submit button only when the form is dirty
    onFormChange(isDirty);
  }, [dirtyFields, isDirty, onFormChange, values]);

  const { mutate: updateTargets, isPending } = useMutation({
    mutationFn: updateHiringGoalTargets,
    onSuccess: async () => {
      notifySuccess({
        message: intl.formatMessage({
          defaultMessage: 'Targets updated successfully!',
        }),
      });

      closeEditTargetsDialog();

      if (currentOpeningSlug) {
        await Promise.all([
          queryClient.invalidateQueries({
            queryKey: [...queryKeys.openingDetails.detail],
          }),
          queryClient.invalidateQueries({
            queryKey: [...queryKeys.openingDetails.channels.list],
          }),
        ]);
      } else {
        await queryClient.invalidateQueries({
          queryKey: [...queryKeys.dashboard.openings],
        });
      }
    },
    onError: () => {
      notifyError({
        message: intl.formatMessage({
          defaultMessage: 'Failed to update targets!',
        }),
      });
    },
  });

  useEffect(() => {
    showSpinner(isPending);
  }, [isPending, showSpinner]);

  const submitForm = () => {
    // Get only changed fields
    const changedFields = Object.keys(dirtyFields).reduce(
      (acc, key) => ({
        ...acc,
        [key]: getValues(key as keyof EditTargetsForm) ?? 0, // Set to 0 if the field is cleared
      }),
      {},
    );

    updateTargets({
      openingSlug: hiringGoal.openingSlug,
      requestBody: changedFields,
    });
  };

  const { is_current: isCurrent } = hiringGoal;

  return (
    <form onSubmit={handleSubmit(submitForm)}>
      <DialogBody className="flex flex-col gap-6 px-6 py-4">
        <HiringGoalDetails hiringGoal={hiringGoal} />

        <div className="bg-fx-neutral-200 h-px w-full" />

        <div className="grid grid-cols-[1fr_24px_1fr] items-center gap-4">
          {/* Target conversion input */}
          <Input
            isNumber
            label={intl.formatMessage({
              defaultMessage: 'Target conversion rate',
            })}
            {...register('expected_conversion', {
              valueAsNumber: true,
              min: {
                value: 0,
                message: intl.formatMessage({
                  defaultMessage: 'Conversion rate must be at least 0%',
                }),
              },
              max: {
                value: 100,
                message: intl.formatMessage({
                  defaultMessage: 'Conversion rate must not exceed 100%',
                }),
              },
            })}
            placeholderLabel="%"
            placeholder={intl.formatMessage({
              defaultMessage: 'Enter target conversion',
            })}
            error={errors.expected_conversion?.message}
            disabled={!isCurrent}
          />
          <img src={ArrowRightIcon} alt="Arrow right" />
          {/* Target applicants details */}
          <div className="mt-5 flex flex-col gap-1">
            <div className="flex items-center gap-1">
              <img src={UserIcon} alt="User" className="h-4 w-4" />
              <Typography variant="subtitle2">
                <FormattedMessage defaultMessage="Target applicants" />
              </Typography>
            </div>
            <Typography variant="body1">
              {targetConversion
                ? Math.round((hiringGoal.goal * 100) / targetConversion)
                : '--'}
            </Typography>
          </div>
        </div>

        {/* Target CPA input */}
        <Input
          isNumber
          label={intl.formatMessage({ defaultMessage: 'Target CPA' })}
          {...register('expected_cpa', { valueAsNumber: true })}
          placeholderLabel="$"
          placeholder={intl.formatMessage({
            defaultMessage: 'Enter target CPA',
          })}
          error={errors.expected_cpa?.message}
          disabled={!isCurrent}
        />

        {/* Target budget input */}
        <TargetBudgetInput
          openingSlug={hiringGoal.openingSlug}
          hookForm={hookForm}
          isCurrent={isCurrent}
        />

        {/* Budget usage card - Progress bar and total spend */}
        <BudgetUsageCard targetBudget={targetBudget} />
      </DialogBody>

      {/* Footer with submit button is passed as a child here */}
      {children}
    </form>
  );
}
